import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, ShieldAlert, AlertTriangle, RefreshCw, Loader2, IndianRupee, X } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

const EMPTY = {
  service_code: "", service_name: "", category: "", description: "", price: "",
  price_type: "FIXED", is_addon: false, parent_service_requirement: "",
  includes: "", excludes: "", conditions: "", active: true,
};

function ItemForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="border border-[#25D366]/40 bg-[#F0FBF3] rounded-lg p-4 space-y-3" data-testid="pricing-item-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[#54656F]">Service code</label>
          <input className={inputCls + " font-mono"} placeholder="OWN_VOCALS_BHAJAN"
            value={f.service_code} onChange={(e) => set("service_code", e.target.value)} data-testid="pi-code" />
        </div>
        <div>
          <label className="text-xs text-[#54656F]">Service / package name</label>
          <input className={inputCls} placeholder="Own Vocals Bhajan"
            value={f.service_name} onChange={(e) => set("service_name", e.target.value)} data-testid="pi-name" />
        </div>
        <div>
          <label className="text-xs text-[#54656F]">Price (₹)</label>
          <input className={inputCls} type="number" placeholder="15000"
            value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="pi-price"
            disabled={f.price_type === "CUSTOM_QUOTE"} />
        </div>
        <div>
          <label className="text-xs text-[#54656F]">Price type</label>
          <select className={inputCls} value={f.price_type} onChange={(e) => set("price_type", e.target.value)} data-testid="pi-type">
            <option value="FIXED">FIXED</option>
            <option value="STARTING_FROM">STARTING_FROM</option>
            <option value="CUSTOM_QUOTE">CUSTOM_QUOTE</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[#54656F]">Includes (comma separated)</label>
          <input className={inputCls} value={f.includes} onChange={(e) => set("includes", e.target.value)} data-testid="pi-includes" />
        </div>
        <div>
          <label className="text-xs text-[#54656F]">Excludes (comma separated)</label>
          <input className={inputCls} value={f.excludes} onChange={(e) => set("excludes", e.target.value)} data-testid="pi-excludes" />
        </div>
      </div>
      <div>
        <label className="text-xs text-[#54656F]">Conditions</label>
        <input className={inputCls} value={f.conditions} onChange={(e) => set("conditions", e.target.value)} data-testid="pi-conditions" />
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-[#111B21]">
          <input type="checkbox" checked={f.is_addon} onChange={(e) => set("is_addon", e.target.checked)} data-testid="pi-addon" />
          This is an add-on (not a standalone package)
        </label>
        <label className="flex items-center gap-2 text-sm text-[#111B21]">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="pi-active" />
          Active
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(f)} data-testid="pi-save"
          className="bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-2 text-sm font-medium">Save</button>
        <button onClick={onCancel} className="text-[#54656F] hover:text-[#111B21] rounded-lg px-4 py-2 text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function PricingSettings() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loadingC, setLoadingC] = useState(false);

  const load = () => api.get("/pricing/items").then((r) => setData(r.data)).catch(() => setData({ items: [], catalog_status: "UNVERIFIED" }));
  const loadConflicts = () => { setLoadingC(true); api.get("/pricing/kb-conflicts").then((r) => setConflicts(r.data)).finally(() => setLoadingC(false)); };
  const loadAlerts = () => api.get("/pricing/alerts?status=open").then((r) => setAlerts(r.data)).catch(() => setAlerts({ alerts: [] }));
  useEffect(() => { load(); loadAlerts(); }, []);

  const toPayload = (f) => ({
    ...f, price: f.price_type === "CUSTOM_QUOTE" ? null : (f.price === "" ? null : Number(f.price)),
    includes: (typeof f.includes === "string" ? f.includes.split(",") : f.includes || []).map((s) => s.trim()).filter(Boolean),
    excludes: (typeof f.excludes === "string" ? f.excludes.split(",") : f.excludes || []).map((s) => s.trim()).filter(Boolean),
  });

  const create = async (f) => {
    try { await api.post("/pricing/items", toPayload(f)); toast.success("Service added"); setAdding(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const update = async (f) => {
    try { await api.put(`/pricing/items/${editing.item_id}`, toPayload(f)); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const toggleVerify = async (it) => {
    await api.post(`/pricing/items/${it.item_id}/verify`, { verified: !it.verified });
    toast.success(!it.verified ? "Verified — will override KB for this service" : "Verification removed");
    load();
  };
  const del = async (it) => { await api.delete(`/pricing/items/${it.item_id}`); load(); };
  const resolveAlert = async (a) => { await api.post(`/pricing/alerts/${a.alert_id}/resolve`); loadAlerts(); };

  if (!data) return <div className="text-sm text-[#8696A0]">Loading…</div>;
  const editRow = editing ? { ...EMPTY, ...editing, includes: (editing.includes || []).join(", "), excludes: (editing.excludes || []).join(", "), price: editing.price ?? "" } : null;

  return (
    <div className="max-w-4xl space-y-6" data-testid="pricing-settings">
      <div className="border border-[#E9EDEF] bg-[#EFF6FF] rounded-lg p-4">
        <p className="text-sm text-[#111B21] font-medium flex items-center gap-2"><IndianRupee className="h-4 w-4 text-[#1D4ED8]" /> How pricing works</p>
        <p className="text-xs text-[#54656F] mt-1 leading-relaxed">
          Vivaan quotes prices from your <b>approved Knowledge Base</b> (primary source). This panel is
          <b> optional</b> — add a structured service here only when you want it to <b>override the KB</b> for that
          exact service. Vivaan can never invent, combine or confirm a price that isn't backed by the KB or a
          verified entry here; unverified prices are blocked and flagged below.
        </p>
      </div>

      {/* Pricing safety alerts */}
      {alerts && alerts.alerts.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 space-y-2" data-testid="pricing-alerts">
          <p className="text-sm font-medium text-amber-900 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Pricing review required ({alerts.alerts.length})</p>
          {alerts.alerts.slice(0, 8).map((a) => (
            <div key={a.alert_id} className="flex items-start justify-between gap-3 text-xs bg-white border border-amber-200 rounded-md px-3 py-2">
              <div>
                <span className={`font-mono px-1.5 py-0.5 rounded ${a.kind === "PRICING_CONFLICT" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{a.kind}</span>
                <span className="text-[#54656F] ml-2">{a.detail}</span>
                {a.customer_query ? <div className="text-[#8696A0] mt-0.5">Customer: “{a.customer_query.slice(0, 90)}”</div> : null}
              </div>
              <button onClick={() => resolveAlert(a)} className="text-[#54656F] hover:text-[#111B21] shrink-0" data-testid={`resolve-${a.alert_id}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Catalog */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading text-base text-[#111B21]">Pricing &amp; Packages <span className="text-xs text-[#8696A0]">(optional)</span></h3>
            <p className="text-xs text-[#8696A0]">{data.items.length} services · {data.verified || 0} verified</p>
          </div>
          {!adding && !editing && (
            <button onClick={() => setAdding(true)} data-testid="pricing-add-btn"
              className="flex items-center gap-1.5 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-3 py-2 text-sm font-medium">
              <Plus className="h-4 w-4" /> Add service
            </button>
          )}
        </div>

        {adding && <ItemForm initial={EMPTY} onSave={create} onCancel={() => setAdding(false)} />}
        {editing && editRow && <ItemForm initial={editRow} onSave={update} onCancel={() => setEditing(null)} />}

        {!adding && !editing && (
          <div className="space-y-2">
            {data.items.length === 0 && (
              <p className="text-sm text-[#8696A0] border border-dashed border-[#E9EDEF] rounded-lg p-6 text-center">
                No structured services yet. That's fine — Vivaan uses your Knowledge Base for pricing. Add one here only to override the KB for a specific service.
              </p>
            )}
            {data.items.map((it) => (
              <div key={it.item_id} className="border border-[#E9EDEF] bg-white rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid={`pricing-row-${it.service_code}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#111B21]">{it.service_name}</span>
                    <span className="font-mono text-[10px] text-[#8696A0]">{it.service_code}</span>
                    {it.is_addon && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">ADD-ON</span>}
                    {!it.active && <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">INACTIVE</span>}
                    {it.verified
                      ? <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> VERIFIED</span>
                      : <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">UNVERIFIED</span>}
                  </div>
                  <p className="text-xs text-[#54656F] mt-0.5">
                    {it.price_type === "CUSTOM_QUOTE" ? "Custom quote" : `₹${it.price} · ${it.price_type}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleVerify(it)} data-testid={`verify-${it.service_code}`}
                    className={`text-xs px-2 py-1 rounded ${it.verified ? "text-amber-700 hover:bg-amber-50" : "text-green-700 hover:bg-green-50"}`}>
                    {it.verified ? "Unverify" : "Verify"}
                  </button>
                  <button onClick={() => setEditing(it)} className="text-xs text-[#1D4ED8] hover:underline">Edit</button>
                  <button onClick={() => del(it)} className="text-[#8696A0] hover:text-red-600" data-testid={`del-${it.service_code}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KB conflicts */}
      <div className="border-t border-[#E9EDEF] pt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-base text-[#111B21] flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Knowledge price conflicts</h3>
          <button onClick={loadConflicts} data-testid="scan-conflicts-btn"
            className="flex items-center gap-1.5 text-sm text-[#1D4ED8] hover:underline">
            {loadingC ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Scan
          </button>
        </div>
        {conflicts === null ? (
          <p className="text-xs text-[#8696A0]">Scan your Knowledge Base for the same service listed with different prices (Vivaan refuses to quote when it finds a conflict).</p>
        ) : conflicts.count === 0 ? (
          <p className="text-xs text-green-700">No conflicting prices found in the Knowledge Base. ✅</p>
        ) : (
          <div className="space-y-2">
            {conflicts.conflicts.map((c) => (
              <div key={c.service} className="border border-red-200 bg-red-50 rounded-md px-3 py-2 text-xs" data-testid={`conflict-${c.service}`}>
                <span className="font-medium text-red-800 capitalize">{c.service}</span>
                <span className="text-red-700 ml-2">{c.prices.map((p) => `₹${p.price}`).join(" vs ")}</span>
                <div className="text-[#8696A0] mt-0.5">Sources: {c.prices.map((p) => p.sources.join("/")).join(" | ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
